import React from 'react';
import { RobotOutlined } from '@ant-design/icons';
import { ListChecks } from 'lucide-react';
import { resolveSkillIcon } from '../../capabilities/capabilityIcons.jsx';


import { parseThinkingProcessLine } from './thinkingProcessPresentation.js';
import { workflowRunState } from './workflowRunProjectionState.js';

import { skillByCapability } from './thinkingProcessActivityProjection.js';

import { resolveWorkflowState } from "./thinkingCardPresentation.jsx";
const ASSISTANT_SECTION_BY_TYPE = new Map([
  ['thinking', 'activity'],
  ['orchestration_status', 'activity'],
  ['message', 'response'],
  ['streaming', 'response'],
]);
const CONCLUSION_LABEL_BY_PROJECTION = new Map([
  [JSON.stringify(['message', 'completed', 'backend_session_projection', 'final_output', false]), '执行结论'],
]);

/** Presentation only: accepted message semantics and Run state remain backend-owned. */
export function resolveAssistantTurnPresentation(items = []) {
  const process = items.findLast((entry) => entry.type === 'thinking') ?? {};
  const runState = workflowRunState(process);
  const { completionEvidenceUncertain } = resolveWorkflowState(process, false);
  return {
    identity: items.find((entry) => entry.messageIdentityKey)?.messageIdentityKey,
    sections: items.map((entry) => ({
      entry,
      section: ASSISTANT_SECTION_BY_TYPE.get(entry.type) ?? 'projection',
      conclusionLabel: CONCLUSION_LABEL_BY_PROJECTION.get(JSON.stringify([
        entry.type, runState, entry.message_projection?.source,
        entry.message_projection?.semantic_kind, completionEvidenceUncertain,
      ])) ?? '',
    })),
  };
}

/** Translate explicit activity/capability identities into existing registered icons. */
export function resolveProcessEntryPresentation({ item, entries, skillProjection }) {
  const activities = new Map((item.processPhases ?? []).map((activity) => [activity.activity_id, activity]));
  const skills = skillByCapability(skillProjection);
  return entries.map((entry) => {
    const activity = activities.get(entry.stepId) ?? {};
    const icons = new Map([
      ['model', <RobotOutlined />],
      ['capability', resolveSkillIcon({ target_id: skills.get(activity.capability_id) })],
    ]);
    return { ...entry, icon: icons.get(activity.kind) ?? <ListChecks size={16} /> };
  });
}

const PROCESS_PRESENTATION_BY_DETAIL = new Map([[true, 'details'], [false, 'compact']]);
const HEADER_OPERATION_TYPES = new Set(['workflow.node.retry']);

function indexWorkflowActions(actions = []) {
  return actions.reduce((index, action) => {
    const target = action?.target;
    const stepId = String(target?.node_id || target?.step_id || '').trim();
    if (!stepId) return index;
    index.set(stepId, [...(index.get(stepId) ?? []), action]);
    return index;
  }, new Map());
}

/** Resolve displayable content once; this does not resolve execution or completion. */
export function resolveProcessLogPresentation({
  workflowProcessEntries = [], activityEntries = [], resourceEntries = [], workflowOperationActions = [],
  onWorkflowOperationAction, processStatus,
}) {
  const activitiesByStepId = new Map(activityEntries.map((entry) => [entry.stepId, entry]));
  const resourcesByLine = new Map(resourceEntries.map((entry) => [entry.line, entry.resources]));
  const workflowActionsByStepId = indexWorkflowActions(workflowOperationActions);
  const entries = workflowProcessEntries.map((entry) => {
    const [headline = '', ...details] = String(entry.line || '').split('\n');
    const parsed = parseThinkingProcessLine(headline);
    const processLine = { ...parsed, title: parsed.text, detailLines: details.filter(Boolean) };
    const activityGroup = activitiesByStepId.get(entry.stepId);
    const resources = resourcesByLine.get(entry.line) ?? [];
    const processActions = workflowActionsByStepId.get(entry.stepId) ?? [];
    const headerActions = processActions.filter((action) => HEADER_OPERATION_TYPES.has(action.operation_type));
    const detailActions = processActions.filter((action) => !HEADER_OPERATION_TYPES.has(action.operation_type));
    const hasDetails = [
      processLine.detailLines.length > 0,
      (activityGroup?.activities?.length ?? 0) > 0,
      resources.length > 0,
      [detailActions.length > 0, typeof onWorkflowOperationAction === 'function'].every(Boolean),
    ].some(Boolean);
    return { ...entry, processLine, activityGroup, resources, processActions: detailActions, headerActions,
      presentation: PROCESS_PRESENTATION_BY_DETAIL.get(hasDetails) };
  });
  const hasProcessRecords = entries.length > 1;
  const presentation = PROCESS_PRESENTATION_BY_DETAIL.get([hasProcessRecords, entries.some((entry) => entry.presentation === 'details')].some(Boolean));
  // Collapse an identical display label, never replace a distinct step outcome.
  const compactStatusByEntry = new Map(entries.slice(-1)
    .filter(({ processLine }) => presentation === 'compact' && processLine.status === processStatus?.statusKey)
    .map((entry) => [entry, processStatus]));
  const compactRunStatus = [processStatus]
    .filter(() => presentation === 'compact' && compactStatusByEntry.size === 0).at(0);
  return { presentation, compactRunStatus,
    entries: entries.map((entry) => ({ ...entry, compactStatus: compactStatusByEntry.get(entry) })) };
}
